/* RESPONSIBLE TEAM: team-standalone */
import Model, { attr } from '@ember-data/model';
import { type ATTRIBUTE_DESCRIPTOR_TYPES } from 'embercom/models/custom-objects/constants/attribute-descriptor-types';
import type Attribute from 'embercom/models/attribute';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { postRequest } from 'embercom/lib/inbox/requests';
import { action } from '@ember/object';

export type IntercomFieldType = 'conversation' | 'user' | 'company';

export default class ExternalFieldIdentifier extends Model {
  @service declare finStandaloneService: FinStandaloneService;

  @attr('boolean') declare builtIn: boolean;
  @attr('string') declare externalId: string;
  @attr('string') declare intercomAttributeIdentifier: string;
  @attr('string') declare intercomDataType: ATTRIBUTE_DESCRIPTOR_TYPES;
  @attr('string') declare intercomFieldType: IntercomFieldType;
  @attr('string') declare name: string;
  @attr('boolean') declare disabledForIdentityMapping: boolean;
  @attr('boolean') declare hiddenForIdentityMapping: boolean;
  @attr('boolean') declare suitableForIdentityMapping: boolean;
  @attr('boolean') declare synchronized: boolean;
  @attr('boolean') declare required: boolean;
  @attr('boolean') declare updateable: boolean;
  @attr('boolean') declare createable: boolean;

  @attr('boolean') declare isSynchronizing?: boolean;

  get attributeModel(): Attribute | undefined {
    if (!this.intercomAttributeIdentifier) {
      return;
    }

    if (this.isUserField || this.isCompanyField) {
      return this.store.peekAll('attribute').findBy('identifier', this.intercomAttributeIdentifier);
    }

    return;
  }

  get intercomCdaIdentifier() {
    if (!this.attributeModel?.isCustomData) {
      return;
    }
    return this.attributeModel?.identifier;
  }

  get intercomSdaIdentifier() {
    if (this.attributeModel?.isCustomData) {
      return;
    }
    return this.attributeModel?.identifier;
  }

  get isCompanyField() {
    return this.intercomFieldType === 'company';
  }

  get isConversationField() {
    return this.intercomFieldType === 'conversation';
  }

  get isUserField() {
    return this.intercomFieldType === 'user';
  }

  get description() {
    return this.attributeModel?.description;
  }

  get type() {
    return this.intercomDataType;
  }

  @action
  async synchronize() {
    let appId = this.finStandaloneService.app.id;
    let integration = this.finStandaloneService.standaloneIntegrations.firstObject!;

    await postRequest(
      `/ember/standalone/integrations/${integration.integrationType}/${integration.id}/synchronize_field?app_id=${appId}`,
      {
        app_id: appId,
        id: this.externalId,
        intercom_field_type: this.intercomFieldType,
      },
    );
    this.synchronized = true;
  }
}

declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'standalone/external-field-identifier': ExternalFieldIdentifier;
  }
}
